import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { User } from '@supabase/supabase-js';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subject, from, map, of, shareReplay, switchMap, takeUntil } from 'rxjs';
import { AnalyticsService } from '../../services/analytics.service';
import { SidebarService } from '../../services/sidebar.service';
import { SupabaseService, Users } from '../../services/supabase.service';
import { Database } from '../../models/database.types';

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule, ButtonModule, TooltipModule, TranslatePipe],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Output() sidebarToggled = new EventEmitter<boolean>();
  user: User | null | undefined;
  user$!: Observable<User | null | undefined>;
  profile$!: Observable<Users | null>;
  isAdmin = false;
  isClubOwner = false;
  isClubOwnerOrAdmin$: Observable<boolean>;
  /** Club owner who never finished the wizard — show the resume entry. */
  needsListingSetup$: Observable<boolean>;
  clubIds: string[] = [];
  sidebarOpen = false;
  isLargeScreen = false;
  private resizeListener = () => this.checkScreenSize();

  /** Up to 2 uppercase initials from a name; falls back to the email's first letter. */
  initials(name?: string | null, email?: string | null): string {
    const n = (name ?? '').trim();
    if (n) {
      return n
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0))
        .join('')
        .toUpperCase();
    }
    return (email ?? '?').charAt(0).toUpperCase();
  }

  constructor(
    private sidebarService: SidebarService,
    private supabaseService: SupabaseService,
    private analytics: AnalyticsService,
    private router: Router,
    private elRef: ElementRef
  ) {
    // Drive the admin section off the USER (reliably set) + user_clubs, NOT the
    // profile role: a freshly onboarded owner has profile.role 'club_owner' and
    // its profile row may lag, but its approved admin user_clubs link exists
    // immediately — otherwise the club settings/requests/stats links stay hidden
    // right after creating the facility.
    const adminState$ = this.supabaseService.user$.pipe(
      switchMap((user) => {
        if (!user) return of({ isAdmin: false, needsSetup: false });
        return from(this.supabaseService.getUserClubs(user.id)).pipe(
          map((clubs) => {
            const adminClubs = (clubs ?? []).filter(
              (c) => c.role === 'admin' || c.role === 'super_admin'
            );
            this.clubIds = adminClubs.map((c) => c.club_id);
            this.isAdmin = adminClubs.length > 0;
            this.isClubOwner = adminClubs.length > 0;
            // Signed up as a club owner (email metadata or OAuth intent) but
            // owns no club yet: the ONLY path back to the wizard, since the
            // signup pages are NoAuthGuard-blocked once logged in.
            const role = (user.user_metadata?.['role'] ?? '') as string;
            return {
              isAdmin: adminClubs.length > 0,
              needsSetup: role === 'club_owner' && adminClubs.length === 0,
            };
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.isClubOwnerOrAdmin$ = adminState$.pipe(map((s) => s.isAdmin));
    this.needsListingSetup$ = adminState$.pipe(map((s) => s.needsSetup));
  }

  ngOnInit() {
    this.user$ = this.supabaseService.user$;
    this.profile$ = this.supabaseService.profile$;
    this.supabaseService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((u) => (this.user = u));

    this.sidebarService.sidebarOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe((open) => (this.sidebarOpen = open));

    this.checkScreenSize();
    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.resizeListener);
  }

  closeSidebar() {
    this.sidebarService.closeSidebar();
  }

  private checkScreenSize() {
    this.isLargeScreen = window.innerWidth >= 1024;
  }

  toggleSidebar() {
    this.sidebarService.toggleSidebar();
    this.sidebarToggled.emit(this.sidebarOpen);
    this.analytics.track('sidebar_toggled', {
      sidebar_open: !this.sidebarOpen,
    });
  }

  // closeSidebar() {
  //   if (this.sidebarOpen) {
  //     this.toggleSidebar();
  //   }
  // }

  async signOut() {
    try {
      this.analytics.track('user_sign_out_attempted', {
        user_id: this.user?.id,
      });
      await this.supabaseService.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      this.analytics.track('user_sign_out_error', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}
